import React, { ReactElement, useEffect, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { OrganizationEntity, UserEntity } from '@data';
import { Roles } from '@app/utils/types';
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from '@ui/components/ui/sidebar';
import { ViewTransitionNavProvider } from '@app/components/core/viewTransition';
import { pageGutterClass } from '@app/constants/styles';
import { ClientSidebar } from './sidebar/client-sidebar';
import SetPasswordPrompt from './SetPasswordPrompt';
import { PromptQueueProvider } from './promptQueue';
import AppSidebar from './sidebar';

export interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  user: UserEntity;
  organizations?: OrganizationEntity[];
}

// The desktop sidebar collapses off-canvas with no inline control to bring it back.
const DesktopSidebarReopen = (): ReactElement | null => {
  const { t } = useTranslation();
  const { isMobile, state } = useSidebar();

  if (isMobile || state === 'expanded') {
    return null;
  }

  return <SidebarTrigger aria-label={t('a11y.openMenu')} className="fixed left-3 top-3 z-20 bg-background shadow-sm" />;
};

/** Out of the way while you read down the page, back as soon as you scroll up or reach the top. */
const useHiddenWhileScrollingDown = (): boolean => {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let last = window.scrollY;
    const onScroll = (): void => {
      const y = window.scrollY;
      if (Math.abs(y - last) < 8) return;
      setHidden(y > last && y > 64);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return hidden;
};

// In reach of the thumb, and off the top of the page where it cost a whole row.
const MobileMenuButton = (): ReactElement => {
  const { t } = useTranslation();
  const hidden = useHiddenWhileScrollingDown();

  return (
    <SidebarTrigger
      aria-label={t('a11y.openMenu')}
      className={cx(
        'fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 size-12 rounded-full border border-border bg-card shadow-lg transition-transform duration-200 md:hidden [&_svg]:size-5',
        hidden && 'translate-y-[calc(100%+2rem)]',
      )}
    />
  );
};

const Layout = ({ children, user, organizations }: LayoutProps): ReactElement => {
  const isClientView = organizations?.[0]?.role === Roles.client;
  // Only a client who has no password: colleagues are created without one and are not asked here.
  const askForPassword = isClientView && user.hasPassword === false;

  if (isClientView) {
    return (
      // Above both the top bar and the routed content, and mounted across a navigation.
      <ViewTransitionNavProvider>
        {/* Above both, so a dialog in the report can hold back anything we raise ourselves. */}
        <PromptQueueProvider>
          <div className="flex w-full min-h-dvh flex-1 flex-row items-stretch bg-background">
            {askForPassword && <SetPasswordPrompt />}
            <ClientSidebar user={user} />
            <main className={`flex min-h-0 min-w-0 flex-1 flex-col self-stretch pb-6 pt-0 ${pageGutterClass}`}>
              <div className="h-16 w-full shrink-0" aria-hidden />
              <div className="flex min-h-0 flex-1 flex-col pt-4 md:pt-6">{children}</div>
            </main>
          </div>
        </PromptQueueProvider>
      </ViewTransitionNavProvider>
    );
  }

  return (
    // Above the sidebar too: it carries its own view-transition-name.
    <ViewTransitionNavProvider>
      <SidebarProvider>
        <AppSidebar user={user} />
        <DesktopSidebarReopen />
        {/* min-w-0 on the inset: a flex item will not shrink below its content's intrinsic width. */}
        <SidebarInset className="min-w-0">
          <MobileMenuButton />
          {/* min-w-0 again here, or a wide grid pushes the whole page past the window. */}
          <div className={`flex min-h-0 min-w-0 flex-1 flex-col pt-4 pb-24 md:pt-6 md:pb-6 ${pageGutterClass}`}>
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </ViewTransitionNavProvider>
  );
};

export default Layout;
